import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { forgetSignedIn, isMarkedSignedIn, markSignedIn, takeLegacyToken } from "../lib/session";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Set right after sign-up so the app can confirm the account was created.
  const [signupConfirmation, setSignupConfirmation] = useState(null);
  const loadedRef = useRef(false);

  const loadMe = useCallback(async () => {
    // A visitor who signed in before sessions moved to a cookie still has
    // their token in browser storage: swap it for the cookie, once.
    const legacyToken = takeLegacyToken();
    if (legacyToken) {
      try {
        await api.post("/auth/session", null, { headers: { Authorization: `Bearer ${legacyToken}` } });
        markSignedIn();
      } catch {
        // The old token was no longer valid — they just sign in again.
      }
    }

    if (!isMarkedSignedIn()) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      // Stale/invalid session (e.g. left over from a reseeded dev DB or a
      // revoked session) — clear it silently and fall back to logged-out.
      forgetSignedIn();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // React 18 StrictMode double-invokes effects in dev; avoid firing the
    // "who am I" request twice, which duplicates 401s in the dev overlay.
    if (loadedRef.current) return;
    loadedRef.current = true;
    loadMe();
  }, [loadMe]);

  // The API has just set the session cookie; remember that we're signed in.
  const signedIn = (nextUser) => {
    markSignedIn();
    setUser(nextUser);
  };

  const login = async (payload) => {
    const { data } = await api.post("/auth/login", payload);
    if (data.requires_2fa) {
      return { requires2fa: true, phone: data.phone, email: data.email, sentTo: data.sent_to };
    }
    signedIn(data.user);
    return data.user;
  };

  const verify2fa = async ({ phone, email, code }) => {
    const { data } = await api.post("/auth/login/verify-2fa", { phone, email, code });
    signedIn(data.user);
    return data.user;
  };

  // Sign-up step 1: checks the form and sends a code. No account yet.
  const requestSignupCode = async (payload) => {
    const { data } = await api.post("/auth/register/code", payload);
    return data.sent_to;
  };

  // Step 2: the same form with the code — this creates the account.
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    signedIn(data.user);
    setSignupConfirmation({ email: data.user.email, sent: Boolean(data.confirmation_sent) });
    return data.user;
  };

  const loginWithGoogle = async (idToken) => {
    const { data } = await api.post("/auth/google", { id_token: idToken });
    signedIn(data.user);
    return data.user;
  };

  const loginWithApple = async (identityToken, fullName) => {
    const { data } = await api.post("/auth/apple", {
      identity_token: identityToken,
      full_name: fullName || undefined,
    });
    signedIn(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore — clearing local state regardless
    }
    forgetSignedIn();
    setUser(null);
  };

  const deleteAccount = async () => {
    await api.delete("/account");
    forgetSignedIn();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, verify2fa, requestSignupCode, register, loginWithGoogle, loginWithApple, logout, deleteAccount, setUser, signedIn, signupConfirmation, setSignupConfirmation }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
